import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { PageWrapper } from '../components/layout/PageWrapper';
import { Button } from '../components/shared/Button';
import { Card } from '../components/shared/Card';
import { Badge } from '../components/shared/Badge';
import { useAppState } from '../hooks/useAppState';
import { PaladinLogo } from '../components/shared/PaladinLogo';
import { mockUser } from '../data/mockUser';

type AuthStep = 'connect' | 'validating' | 'validated' | 'denied';

export function Auth() {
  const navigate = useNavigate();
  const { setUser } = useAppState();
  const [step, setStep] = useState<AuthStep>('connect');

  function handleDiscordConnect() {
    setStep('validating');
    setTimeout(() => {
      setStep('validated');
    }, 1200);
  }

  function handleContinue() {
    setUser({ ...mockUser, traderStage: null, currentStrategy: null, currentFirmId: null, currentTier: null });
    navigate('/assess');
  }

  return (
    <PageWrapper>
      <Navbar />
      <main className="min-h-screen flex items-center justify-center px-6 pt-14">
        <Card className="w-full max-w-[420px] text-center">
          <PaladinLogo size={56} className="mx-auto mb-4" />

          {step === 'connect' && (
            <>
              <h2 className="text-lg font-semibold text-white/90 mb-2">Discord Verification</h2>
              <p className="text-sm text-white/40 mb-6">
                Connect your Discord account to verify your Inevitrade membership.
              </p>
              <Button onClick={handleDiscordConnect} className="w-full flex items-center justify-center gap-2">
                <MessageCircle className="w-4 h-4" />
                Connect Discord
              </Button>
            </>
          )}

          {step === 'validating' && (
            <>
              <h2 className="text-lg font-semibold text-white/90 mb-2">Verifying...</h2>
              <p className="text-sm text-white/40 mb-4">
                Checking your Discord role...
              </p>
              <div className="flex justify-center">
                <div className="w-6 h-6 border-2 border-[#4A9EFF] border-t-transparent rounded-full animate-spin" />
              </div>
            </>
          )}

          {step === 'validated' && (
            <>
              <h2 className="text-lg font-semibold text-white/90 mb-2">Verified</h2>
              <p className="text-sm text-white/40 mb-4">
                Welcome, <span className="text-white">{mockUser.discordUsername}</span>
              </p>
              <div className="flex justify-center mb-6">
                <Badge
                  label={mockUser.role}
                  color={mockUser.role === 'Inner Circle' ? '#D4A843' : '#4A9EFF'}
                />
              </div>
              <Button onClick={handleContinue} className="w-full">
                Continue
              </Button>
            </>
          )}

          {step === 'denied' && (
            <>
              <h2 className="text-lg font-semibold text-[#E84040] mb-2">Access Denied</h2>
              <p className="text-sm text-white/40 mb-6">
                You must be an Accelerator or Inner Circle member to access Paladin.
                Join the Inevitrade Discord to get started.
              </p>
              <Button variant="secondary" onClick={() => setStep('connect')} className="w-full">
                Try Again
              </Button>
            </>
          )}
        </Card>
      </main>
    </PageWrapper>
  );
}
